import { Typography, useToastStore } from '@branch-messenger/willow-ui'
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { use } from 'react'
import { useTheme } from 'styled-components'

import {
  downloadAdvanceAgreement,
  getAdvanceActivity,
  getBranchVerification,
  getPayrollSchedules,
  getReportedEarnings,
  getReportedHours,
  getTransactionBalance,
  postAdvanceQuote,
  requestAdvance,
} from '@/api/ewa'
import { DialogContext } from '@/context'
import { failedAdvanceArticleUrl } from '@/lib/urls'
import {
  AdvanceActivityParams,
  AdvanceQuoteRequest,
  AdvanceRequest,
  ReportedHoursParams,
} from '@/types/ewa'

export const EWA_BRANCH_VERIFICATION_QUERY = 'ewa-branch-verification-query'

export const ewaBranchVerificationQueryOptions = queryOptions({
  queryKey: [EWA_BRANCH_VERIFICATION_QUERY],
  queryFn: async () => {
    const { data } = await getBranchVerification()
    return data
  },
})

export const EWA_TRANSACTION_BALANCE_QUERY = 'ewa-transaction-balance-query'

export const ewaTransactionBalanceQueryOptions = queryOptions({
  queryKey: [EWA_TRANSACTION_BALANCE_QUERY],
  queryFn: async () => {
    const { data } = await getTransactionBalance()
    return data
  },
})

export const EWA_PAYROLL_SCHEDULES_QUERY = 'ewa-payroll-schedules-query'

export const ewaPayrollScheduleQueryOptions = queryOptions({
  queryKey: [EWA_PAYROLL_SCHEDULES_QUERY],
  queryFn: async () => {
    const { data } = await getPayrollSchedules()
    return data
  },
})

export const GET_ADVANCES_QUERY = 'GET_ADVANCES_QUERY'
export const advancesQueryOptions = (params?: AdvanceActivityParams) =>
  infiniteQueryOptions({
    queryKey: [GET_ADVANCES_QUERY, params],
    queryFn: async ({ pageParam }) => {
      const { data } = await getAdvanceActivity({
        size: 10,
        page: pageParam,
        ...params,
      })

      return data
    },
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage?.page_number < lastPage.total_pages
        ? lastPage?.page_number + 1
        : undefined,
  })

export const REPORTED_HOURS_QUERY = 'REPORTED_HOURS_QUERY'
export const reportedHoursQueryOptions = (
  params: Partial<ReportedHoursParams> &
    Pick<ReportedHoursParams, 'start_time' | 'end_time'>
) =>
  infiniteQueryOptions({
    queryKey: [REPORTED_HOURS_QUERY, params],
    queryFn: async ({ pageParam }) => {
      const { data } = await getReportedHours({
        size: 10,
        page: pageParam,
        sort: 'date',
        direction: 'desc',
        ...params,
      })
      return data
    },
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage?.page_number < lastPage.total_pages
        ? lastPage?.page_number + 1
        : undefined,
  })

export const REPORTED_EARNINGS_QUERY = 'REPORTED_EARNINGS_QUERY'
export const reportedEarningsQueryOptions = infiniteQueryOptions({
  queryKey: [REPORTED_EARNINGS_QUERY],
  queryFn: async ({ pageParam }) => {
    const { data } = await getReportedEarnings({
      size: 10,
      page: pageParam,
    })
    return data
  },
  initialPageParam: 1,
  getNextPageParam: lastPage =>
    lastPage?.page_number < lastPage.total_pages
      ? lastPage?.page_number + 1
      : undefined,
})

export const useDownloadAdvanceAgreement = () => {
  const addToast = useToastStore(state => state.addToast)
  return useMutation({
    mutationFn: async (advanceId: string) => {
      const { data } = await downloadAdvanceAgreement(advanceId)
      const fileBlob = new Blob([data], { type: 'application/pdf' })
      const href = window.URL.createObjectURL(fileBlob)
      const downloadPdf = document.createElement('a')
      downloadPdf.href = href
      const filename = `advance_agreement_${advanceId}.pdf`
      downloadPdf.download = filename
      downloadPdf.click()
    },
    onSuccess: () => {
      addToast({
        title: 'Success',
        description: 'Your advance agreement has been downloaded.',
        variant: 'success',
      })
    },
    onError: () => {
      addToast({
        title: 'Error',
        description: 'There was an error downloading your advance agreement.',
        variant: 'alert',
      })
    },
  })
}

export const ADVANCE_QUOTE_QUERY = 'advance-quote-query'

export const advanceQuoteQueryOptions = (request: AdvanceQuoteRequest) =>
  queryOptions({
    queryKey: [ADVANCE_QUOTE_QUERY, request.requested_advance_amount],
    queryFn: async () => {
      const { data } = await postAdvanceQuote(request)
      return data
    },
    placeholderData: keepPreviousData,
    enabled: request.requested_advance_amount > 0,
  })

export const useRequestAdvance = () => {
  const queryClient = useQueryClient()
  const theme = useTheme()
  const { showAlert } = use(DialogContext)
  return useMutation({
    mutationFn: async (body: AdvanceRequest) => {
      const res = await requestAdvance(body)
      return res.data
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_ADVANCES_QUERY] })
      queryClient.invalidateQueries({
        queryKey: [EWA_TRANSACTION_BALANCE_QUERY],
      })
      queryClient.invalidateQueries({
        queryKey: [EWA_BRANCH_VERIFICATION_QUERY],
      })
    },

    onError: () => {
      showAlert({
        title: 'Unable To Submit',
        description: (
          <>
            <Typography style={{ marginBottom: theme.space[2] }}>
              We are unable to submit your request at this time.{' '}
            </Typography>
            <Typography>
              It’s possible the issue may be solved by waiting a few minutes and
              trying again, but often times, these errors will stem from issues
              with your connected card. Learn more about common card issues and
              steps to troubleshoot in our{' '}
              <a
                href={failedAdvanceArticleUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Account Management support article
              </a>
              .
            </Typography>
          </>
        ),
        actionDescription: 'OK',
        hideCancel: true,
      })
    },
  })
}
